import { BeneficiaryCountry, PayrollStatus } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { useState } from 'react';
import useSWRMutation from 'swr/mutation';
import { useData } from '../../../contexts/data-context/use-data';
import { CurrencyCode, PayrollView } from '../../../types/entity-types';
import { postSwrFetcher } from '../../../utils/post-swr-fetcher';
import { postSwrFetcherWithDynamicUrl } from '../../../utils/post-swr-fetcher-with-dynamic-url';
import { putSwrFetcherWithDynamicUrl } from '../../../utils/put-swr-fetcher-with-dynamic-url';

const PAYROLLS_API_PATH = '/api/payrolls';

type PayrollPaymentRequestType = {
  sourceAccountId: string;
  totalAmount: number;
};

type PayrollRequestType = {
  name: string;
  period: string;
  country: BeneficiaryCountry;
  currency: CurrencyCode;
  reference: string;
  companyId: string;
  copiedFromPayrollId?: string;
  status?: PayrollStatus;
};

export type PayrollSetupFormData = {
  country: string;
  currency: string;
  payrollName: string;
  payrollTimePeriod: string;
  payrollReference: string;
  status?: PayrollStatus;
  copiedFromPayrollId?: string;
};

export const usePayroll = () => {
  const [isCreatingPayroll, setIsCreatingPayroll] = useState(false);
  const { data, isLoading: isLoadingData, error: dataError, mutations } = useData();
  const { trigger: createPayroll } = useSWRMutation<PayrollView, Error, string, PayrollRequestType>(
    PAYROLLS_API_PATH,
    postSwrFetcher,
  );
  const { trigger: updatePayroll, isMutating: isUpdatingPayroll } = useSWRMutation<
    PayrollView,
    AxiosError,
    string,
    { urlSuffix: string } & Partial<PayrollRequestType>
  >(PAYROLLS_API_PATH, putSwrFetcherWithDynamicUrl);
  const { trigger: createPayrollPayment, isMutating: isCreatingPayrollPayment } = useSWRMutation<
    { status: string; authorisationUrl: string },
    AxiosError,
    string,
    { urlSuffix: string } & PayrollPaymentRequestType
  >(PAYROLLS_API_PATH, postSwrFetcherWithDynamicUrl);

  const handleCreatePayroll = async (payrollSetupData: PayrollSetupFormData) => {
    if (!data?.companies?.[0]?.id) {
      throw new Error('No company found');
    }

    const payrollData: PayrollRequestType = {
      name: payrollSetupData.payrollName,
      period: payrollSetupData.payrollTimePeriod,
      country: payrollSetupData.country as BeneficiaryCountry,
      currency: payrollSetupData.currency as CurrencyCode,
      reference: payrollSetupData.payrollReference,
      companyId: data.companies[0].id,
      copiedFromPayrollId: payrollSetupData.copiedFromPayrollId,
    };

    try {
      setIsCreatingPayroll(true);
      const newPayroll = await createPayroll(payrollData);
      await mutations.handleIncrementalUpdates();
      return newPayroll;
    } catch (error) {
      Sentry.captureException(error, {
        extra: { payrollData },
        tags: { action: 'handleCreatePayroll' },
      });
      throw error;
    } finally {
      setIsCreatingPayroll(false);
    }
  };

  const handleUpdatePayroll = async (payrollId: string, payrollData: Partial<PayrollSetupFormData>) => {
    try {
      // new to cast to PayrollRequestType object since Payroll is linked with circular dependencies
      const payload = {
        name: payrollData.payrollName,
        period: payrollData.payrollTimePeriod,
        country: payrollData.country as BeneficiaryCountry,
        currency: payrollData.currency as CurrencyCode,
        reference: payrollData.payrollReference,
        status: payrollData.status,
      };

      const updatedPayroll = await updatePayroll({ urlSuffix: `/${payrollId}`, ...payload });
      await mutations.handleIncrementalUpdates();
      return updatedPayroll;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          payrollId,
          payrollData,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
          errorStack: error instanceof Error ? error.stack : undefined,
        },
        tags: {
          component: 'usePayroll',
          action: 'handleUpdatePayroll',
        },
      });
      throw error;
    }
  };

  const handlePayrollPayment = async (payrollId: string, paymentData: PayrollPaymentRequestType) => {
    try {
      const updatedPayroll = await createPayrollPayment({ urlSuffix: `/${payrollId}/payments`, ...paymentData });
      await mutations.handleIncrementalUpdates();

      return updatedPayroll;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          payrollId,
          paymentData,
        },
      });
      throw error;
    }
  };

  return {
    data,
    isLoading: isLoadingData || isCreatingPayrollPayment || isUpdatingPayroll || isCreatingPayroll,
    error: dataError,
    handleCreatePayroll,
    handleUpdatePayroll,
    handlePayrollPayment,
  };
};
